/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 23.05.25
 *
 */
import BaseObject from "@shared/BaseObject.js";
import { syncNodeLabel } from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/genogramUtils";

const minChildSpacing = 120;
const parentChildMargin = 120;
const verticalSpacing = 300;

export default class GenogramLayoutManager extends BaseObject {


	constructor() {
		super();
	}


	layoutFamilyTree(json) {
		const { people, marriages, allPeopleIds, rootId } = this.parseTree(json.instructions);

		if (rootId) {
			this.assignGeneration(people, rootId, 0);
		}
		allPeopleIds.forEach(pid => this.computeSubtreeWidth(people, marriages, allPeopleIds, pid));
		if (rootId) {
			this.layout(people, marriages, allPeopleIds, rootId, 0);
		}
		Object.values(people).forEach(p => {
			p.y = p.gen * verticalSpacing;
		});
		Object.values(marriages).forEach(([parent1, parent2]) => {
			const y = Math.min(people[parent1].y, people[parent2].y);
			people[parent1].y = y;
			people[parent2].y = y;
		});

		let nodes = Object.values(people).map(p => ({
			id: p.id,
			data: {
				name: p.name,
				type: "male",
			},
			position: {
				x: p.x,
				y: p.y
			},
		}));

		nodes.forEach(node => {
			syncNodeLabel(node.data)
		});
		return nodes;
	}

	parseTree(instructions) {
		const people = {};
		const marriages = {};

		for (const instr of instructions) {
			if (instr.action === "addPerson") {
				people[instr.properties.id] = {
					...instr.properties,
					children: [],
					parents: [],
					spouses: [],
					x: 0,
					y: 0,
					gen: undefined
				};
			}
		}
		for (const instr of instructions) {
			if (instr.action === "addConnection" && instr.properties.type === "marriage") {
				marriages[instr.properties.id] = [instr.properties.fromId, instr.properties.toId];
				people[instr.properties.fromId].spouses.push(instr.properties.toId);
				people[instr.properties.toId].spouses.push(instr.properties.fromId);
			}
		}
		for (const instr of instructions) {
			if (instr.action === "addConnection" && instr.properties.type === "biological_child") {
				const marriage = marriages[instr.properties.toId];
				if (!marriage) continue;
				const [parent1, parent2] = marriage;
				people[parent1].children.push(instr.properties.fromId);
				people[parent2].children.push(instr.properties.fromId);
				people[instr.properties.fromId].parents.push(parent1, parent2);
			}
		}
		const allPeopleIds = Object.keys(people);
		const allChildrenIds = new Set();
		allPeopleIds.forEach(pid => {
			people[pid].children.forEach(cid => allChildrenIds.add(cid));
		});
		const rootId = allPeopleIds.find(pid => !allChildrenIds.has(pid));
		return { people, marriages, allPeopleIds, rootId };
	}

	assignGeneration(people, personId, gen, visited = new Set()) {
		if (visited.has(personId)) return;
		visited.add(personId);
		const person = people[personId];
		person.gen = gen;
		for (const spouseId of person.spouses) {
			if (people[spouseId].gen === undefined) {
				this.assignGeneration(people, spouseId, gen, visited);
			}
		}
		for (const childId of person.children) {
			if (people[childId].gen === undefined) {
				this.assignGeneration(people, childId, gen + 1, visited);
			}
		}
		for (const parentId of person.parents) {
			if (people[parentId] && people[parentId].gen === undefined) {
				this.assignGeneration(people, parentId, gen - 1, visited);
			}
		}
	}

	computeSubtreeWidth(people, marriages, allPeopleIds, personId, visited = new Set()) {
		if (visited.has(personId)) return 0;
		visited.add(personId);
		const person = people[personId];
		if (person.children.length === 0) {
			person._subtreeWidth = minChildSpacing;
			return person._subtreeWidth;
		}
		let childGroups = [];
		for (const marriageId in marriages) {
			const [p1, p2] = marriages[marriageId];
			if (p1 === personId || p2 === personId) {
				const kids = allPeopleIds.filter(pid => {
					const p = people[pid];
					return p.parents.includes(p1) && p.parents.includes(p2);
				});
				if (kids.length) childGroups.push(kids);
			}
		}
		if (childGroups.length === 0) {
			childGroups = [person.children];
		}
		let width = 0;
		for (const group of childGroups) {
			if (group.length === 0) continue;
			let groupWidth = 0;
			for (const childId of group) {
				groupWidth += this.computeSubtreeWidth(people, marriages, allPeopleIds, childId, visited);
			}
			groupWidth += (group.length - 1) * minChildSpacing;
			width = Math.max(width, groupWidth);
		}
		person._subtreeWidth = Math.max(width, minChildSpacing);
		return person._subtreeWidth;
	}

	layout(people, marriages, allPeopleIds, personId, x, visited = new Set()) {
		if (visited.has(personId)) return;
		visited.add(personId);
		const person = people[personId];
		if (person.children.length === 0) {
			person.x = x;
			return;
		}
		let marriagesForPerson = [];
		for (const marriageId in marriages) {
			const [p1, p2] = marriages[marriageId];
			if (p1 === personId || p2 === personId) {
				marriagesForPerson.push([marriageId, p1, p2]);
			}
		}
		if (marriagesForPerson.length === 0) {
			let totalWidth = 0;
			for (const childId of person.children) {
				totalWidth += people[childId]._subtreeWidth;
			}
			totalWidth += (person.children.length - 1) * minChildSpacing;
			let startX = x - totalWidth / 2;
			for (const childId of person.children) {
				const childWidth = people[childId]._subtreeWidth;
				this.layout(people, marriages, allPeopleIds, childId, startX + childWidth / 2, visited);
				startX += childWidth + minChildSpacing;
			}
			person.x = x;
			return;
		}
		for (const [marriageId, leftParent, rightParent] of marriagesForPerson) {
			const kids = allPeopleIds.filter(pid => {
				const p = people[pid];
				return p.parents.includes(leftParent) && p.parents.includes(rightParent);
			});
			if (kids.length === 0) continue;
			let totalKidsWidth = 0;
			for (const kidId of kids) {
				totalKidsWidth += people[kidId]._subtreeWidth;
			}
			totalKidsWidth += (kids.length - 1) * minChildSpacing;
			let startX = x - totalKidsWidth / 2;
			let childCenters = [];
			for (const kidId of kids) {
				const childWidth = people[kidId]._subtreeWidth;
				const childCenter = startX + childWidth / 2;
				childCenters.push(childCenter);
				this.layout(people, marriages, allPeopleIds, kidId, childCenter, visited);
				startX += childWidth + minChildSpacing;
			}
			// Always place leftParent to the left, rightParent to the right
			const leftmostChildX = childCenters[0];
			const rightmostChildX = childCenters[childCenters.length - 1];
			people[leftParent].x = leftmostChildX - parentChildMargin;
			people[rightParent].x = rightmostChildX + parentChildMargin;
			// Center the family at x
			const familyCenter = (people[leftParent].x + people[rightParent].x) / 2;
			const shift = x - familyCenter;
			people[leftParent].x += shift;
			people[rightParent].x += shift;
			for (let i = 0; i < kids.length; i++) {
				people[kids[i]].x += shift;
			}
		}
		if (marriagesForPerson.length === 0) {
			person.x = x;
		}
	}
}
